import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchSelect } from '@/components/ui/search-select';
import { addDeployment, getSchoolOptions, getV1Progress } from '@/api/schools';
import { V1DeploymentStatus } from './V1DeploymentStatus';
import type { SchoolOptions } from '@/types/school';
import type { V1SetupStatus } from '@/api/schools';
import { Loader2, Server, AlertTriangle } from 'lucide-react';

interface V1DeployDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolCode: string;
  schoolName: string;
  onSuccess?: () => void;
}

type DialogStep = 'loading' | 'form' | 'deploying' | 'progress';

export function V1DeployDialog({
  open,
  onOpenChange,
  schoolCode,
  schoolName,
  onSuccess,
}: V1DeployDialogProps) {
  const [step, setStep] = useState<DialogStep>('loading');
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupStatus, setSetupStatus] = useState<V1SetupStatus | null>(null);
  const [options, setOptions] = useState<SchoolOptions | null>(null);

  // Form data
  const [formStatus, setFormStatus] = useState('demo_empty');
  const [schoolSourceId, setSchoolSourceId] = useState<number | null>(null);
  const [moduleIds, setModuleIds] = useState<number[]>([]);

  useEffect(() => {
    if (!open) return;

    setStep('loading');
    setError(null);
    setLoading(true);
    setSetupStatus(null);

    Promise.all([
      getSchoolOptions(),
      getV1Progress(schoolCode).catch(() => null),
    ])
      .then(([optionsResult, progressResult]) => {
        setOptions(optionsResult);

        // If V1 deployment is already in progress or completed, show progress
        if (progressResult?.deployed && progressResult.status !== 'not_started') {
          setSetupStatus(progressResult.status);
          setStep('progress');
        } else {
          setStep('form');
        }
      })
      .catch((err) => {
        setError((err as Error).message || 'Erreur lors du chargement');
        setStep('form');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, schoolCode]);

  const handleModuleToggle = (moduleId: number) => {
    setModuleIds((prev) =>
      prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId]
    );
  };

  const handleDeploy = async () => {
    setDeploying(true);
    setError(null);
    setStep('deploying');

    try {
      await addDeployment(schoolCode, {
        version: 'v1',
        status: formStatus,
        schoolSourceId: schoolSourceId || undefined,
        moduleIds: moduleIds.length > 0 ? moduleIds : undefined,
      });
      setStep('progress');
    } catch (err) {
      setError((err as Error).message || 'Erreur lors du déploiement');
      setStep('form');
    } finally {
      setDeploying(false);
    }
  };

  const isDeploymentRunning = step === 'deploying' || (step === 'progress' && setupStatus === 'in_progress');

  const handleClose = () => {
    if (isDeploymentRunning) return;
    onOpenChange(false);
    if (step === 'progress' || setupStatus === 'completed') {
      onSuccess?.();
    }
  };

  const handleStatusChange = (status: V1SetupStatus | null) => {
    setSetupStatus(status);
  };

  const getDialogTitle = () => {
    if (step === 'deploying') return 'Déploiement V1 en cours';
    if (step === 'progress') {
      if (setupStatus === 'failed') return 'Déploiement V1 échoué';
      if (setupStatus === 'completed') return 'Déploiement V1 terminé';
      if (setupStatus === 'in_progress') return 'Déploiement V1 en cours';
      return 'Statut du déploiement V1';
    }
    return 'Déployer en V1';
  };

  const getDialogDescription = () => {
    if (step === 'deploying') {
      return (
        <>
          Déploiement de <strong>{schoolName}</strong> en cours...
        </>
      );
    }
    if (step === 'progress') {
      if (setupStatus === 'failed') {
        return (
          <>
            Le déploiement de <strong>{schoolName}</strong> a échoué. Vous pouvez relancer.
          </>
        );
      }
      return (
        <>
          Suivi du déploiement de <strong>{schoolName}</strong>
        </>
      );
    }
    return (
      <>
        Déployer l'école <strong>{schoolName}</strong> en V1
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={`sm:max-w-[600px] max-h-[85vh] flex flex-col ${isDeploymentRunning ? '[&>button.absolute]:hidden' : ''}`}
        onInteractOutside={(e) => { if (isDeploymentRunning) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isDeploymentRunning) e.preventDefault(); }}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          {/* Loading state */}
          {step === 'loading' && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Chargement...</span>
            </div>
          )}

          {/* Deploying state */}
          {step === 'deploying' && (
            <div className="py-4">
              <V1DeploymentStatus
                schoolCode={schoolCode}
                onStatusChange={handleStatusChange}
                autoRefresh={true}
                refreshInterval={2000}
              />
            </div>
          )}

          {/* Form step */}
          {step === 'form' && options && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={formStatus} onValueChange={setFormStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options.statuses.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>École source</Label>
                <SearchSelect
                  options={options.sourceSchools.map((school) => ({
                    value: school.id.toString(),
                    label: school.isDefault ? school.name : `${school.name} (${school.code})`,
                    searchText: `${school.name} ${school.code}`,
                  }))}
                  value={schoolSourceId?.toString() || (options.sourceSchools.find(s => s.isDefault)?.id.toString() ?? '')}
                  onValueChange={(value) => setSchoolSourceId(parseInt(value))}
                  placeholder="Sélectionner une école source"
                />
              </div>

              <div className="space-y-2">
                <Label>
                  Modules ({options.modules.filter((m) => !m.isV2 && moduleIds.includes(m.id)).length})
                </Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {options.modules
                    .filter((m) => !m.isV2)
                    .map((module) => (
                      <div
                        key={module.id}
                        className="flex items-center space-x-2 p-2 rounded border bg-white hover:bg-muted/50"
                      >
                        <Checkbox
                          id={`deploy-module-${module.id}`}
                          checked={moduleIds.includes(module.id)}
                          onCheckedChange={() => handleModuleToggle(module.id)}
                        />
                        <Label
                          htmlFor={`deploy-module-${module.id}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {module.name}
                        </Label>
                      </div>
                    ))}
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Progress step */}
          {step === 'progress' && (
            <div className="py-4">
              <V1DeploymentStatus
                schoolCode={schoolCode}
                onStatusChange={handleStatusChange}
                autoRefresh={true}
                refreshInterval={2000}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="flex-shrink-0 flex-col sm:flex-row gap-2 border-t pt-4">
          {step === 'form' && (
            <>
              <Button variant="outline" onClick={handleClose} disabled={deploying}>
                Annuler
              </Button>
              <Button onClick={handleDeploy} disabled={loading || deploying}>
                {deploying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Déploiement...
                  </>
                ) : (
                  <>
                    <Server className="h-4 w-4 mr-2" />
                    Déployer
                  </>
                )}
              </Button>
            </>
          )}
          {(step === 'deploying' || step === 'progress') && !isDeploymentRunning && (
            <Button variant="outline" onClick={handleClose}>
              {setupStatus === 'completed' ? 'Terminé' : 'Fermer'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
